import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, Calendar, Users, Building2, UserPlus, BookOpen, Sparkles } from 'lucide-react';
import ThemeSwitcher from './ThemeSwitcher';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [currentTheme, setCurrentTheme] = useState('dim');
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Listen for theme changes to swap logo
  useEffect(() => {
    const checkTheme = () => {
      const theme = localStorage.getItem('theme') || 'dim';
      setCurrentTheme(theme);
    };
    checkTheme();
    window.addEventListener('storage', checkTheme);
    // Also check on interval for immediate theme switcher changes
    const interval = setInterval(checkTheme, 500);
    return () => {
      window.removeEventListener('storage', checkTheme);
      clearInterval(interval);
    };
  }, []);

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/research', label: 'Research', icon: BookOpen },
    { path: '/vernand-morency', label: 'Vernand Morency', icon: Users },
    { path: '/speakers', label: 'Speakers', icon: Users },
    { path: '/experience', label: 'Experience', icon: Calendar },
    { path: '/agenda', label: 'Agenda', icon: Calendar },
    { path: '/sponsorship', label: 'Partnership', icon: Building2 },
  ];

  const isActive = (path) => location.pathname === path;

  // Select logo based on theme
  // Logo naming: "Light" = for light backgrounds (dark logo), "Dark" = for dark backgrounds (light logo)
  const logoSrc = currentTheme === 'light' 
    ? '/Logo_Horizontal_Light_Transparent.png'  // Dark colored logo for light background
    : '/Logo_Horizontal_Dark_Transparent.png';  // Light colored logo for dark background

  return (
    <nav
      className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 transition-all duration-500 ${
        scrolled ? 'w-[96%] max-w-7xl' : 'w-[94%] max-w-7xl'
      }`}
      data-testid="main-navigation"
    >
      <div className="rounded-full px-4 md:px-6 py-2 md:py-3 flex items-center justify-between bg-background/90 backdrop-blur-xl border border-border/50 shadow-lg overflow-hidden">
        {/* Official Brand Logo */}
        <Link 
          to="/" 
          className="flex items-center gap-3 group flex-shrink-0"
          data-testid="nav-logo"
        >
          <img 
            src={logoSrc}
            alt="Quantum Housing Conference"
            className={`w-auto object-contain transition-all duration-300 group-hover:scale-105 ${
              scrolled 
                ? 'h-8 md:h-10 lg:h-12 min-w-[100px] md:min-w-[120px] lg:min-w-[140px]' 
                : 'h-10 md:h-12 lg:h-14 xl:h-16 min-w-[120px] md:min-w-[150px] lg:min-w-[180px]'
            }`}
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-0.5 flex-shrink min-w-0">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
              className={`flex items-center gap-1 px-2.5 xl:px-3 py-1.5 rounded-full text-xs xl:text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                isActive(link.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground/80 hover:text-foreground hover:bg-foreground/5'
              }`}
            >
              <link.icon className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
              <span className="hidden xl:inline">{link.label}</span>
              <span className="xl:hidden">{link.label.split(' ')[0]}</span>
            </Link>
          ))}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-1.5 md:gap-2 flex-shrink-0">
          <Link
            to="/signup"
            className="hidden sm:flex items-center gap-1.5 px-3 xl:px-4 py-1.5 xl:py-2 bg-primary text-primary-foreground rounded-full text-xs xl:text-sm font-medium transition-transform hover:-translate-y-0.5"
            data-testid="nav-register-btn"
          >
            <UserPlus className="w-3.5 h-3.5 xl:w-4 xl:h-4" />
            <span className="hidden md:inline">Register</span>
          </Link>
          <ThemeSwitcher />
          
          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-full hover:bg-foreground/5 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div 
          className="lg:hidden mt-2 rounded-2xl p-4 animate-in slide-in-from-top-2 duration-300 bg-background/95 backdrop-blur-xl border border-border/50 shadow-lg"
          data-testid="mobile-menu"
        >
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                isActive(link.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-foreground/5'
              }`}
            >
              <link.icon className="w-5 h-5" />
              {link.label}
            </Link>
          ))}
          <Link
            to="/signup"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl mt-2 bg-primary text-primary-foreground"
          >
            <UserPlus className="w-5 h-5" />
            Register
          </Link>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
